/** Source-of-truth badges for Desk tiles.
 *
 *  Every Desk tile carries — LEFT of the header — a small badge showing which
 *  service its data comes from. External services (Xero, Microsoft 365, Stripe,
 *  Fieldy) wear that service's official mark. TravelTrucks-native tiles (Now,
 *  Pipeline, My Lists, Ideas, Quick Capture, Desk AI, Builds, etc.) wear the
 *  TravelTrucks mark so at a glance you know it's OS-native, not external.
 *
 *  Adding a new tile? Register its source in TILE_SOURCE_MAP below.
 *  Unknown ids default to 'traveltrucks' (safe fallback — every tile is at
 *  least OS-native), so no tile ever renders unbadged or with a broken image.
 */
import React, { useState } from 'react';

// ── The registry — one entry per Desk tile id ──────────────────────────
export const TILE_SOURCE_MAP = {
  // Xero-fed
  moneyxero:       'xero',
  moneyxerodetail: 'xero',
  invoices:        'xero',
  quotesaging:     'xero',

  // Microsoft 365 (Graph) — Outlook mail + calendar
  inbox:      'microsoft',
  awaiting:   'microsoft',
  calendar:   'microsoft',

  // Stripe
  payments:   'stripe',

  // GoHighLevel — enquiries / CRM / pipeline
  enquiries:  'ghl',
  pipeline:   'ghl',

  // Workflow Max — jobs / fitting-bay / WIP
  jobs:       'workflowmax',
  fittingbay: 'workflowmax',

  // Fieldy — 5pm debrief + today's actions
  debrief:    'fieldy',
  actions:    'fieldy',

  // TravelTrucks-native (default, but declared for clarity)
  now:        'traveltrucks',
  pulse:      'traveltrucks',
  clients:    'traveltrucks',
  money:      'traveltrucks',
  parking:    'traveltrucks',
  wins:       'traveltrucks',
  ai:         'traveltrucks',
  capture:    'traveltrucks',
  notes:      'traveltrucks',
  clock:      'traveltrucks',
  winsweek:   'traveltrucks',
  sendstoday: 'traveltrucks',
  promises:   'traveltrucks',
  focustimer: 'traveltrucks',
  quicklinks: 'traveltrucks',
  weather:    'traveltrucks',
  projects:   'traveltrucks',
  llmbudget:  'traveltrucks',
  sweepaudit: 'traveltrucks',
  wonchart:   'traveltrucks',
  repliestrend: 'traveltrucks',
};

// ── Per-source display config ──────────────────────────────────────────
const SOURCE_META = {
  xero:      { src: '/source-marks/xero.svg',      label: 'Xero',           short: 'X' },
  microsoft: { src: '/source-marks/microsoft.svg', label: 'Microsoft 365',  short: 'MS' },
  stripe:    { src: '/source-marks/stripe.svg',    label: 'Stripe',         short: 'S' },
  ghl:       { src: '/source-marks/ghl.svg',       label: 'GoHighLevel',    short: 'GHL' },
  workflowmax: { src: '/source-marks/workflowmax.svg', label: 'Workflow Max', short: 'WM' },
  fieldy:    { src: '/source-marks/fieldy.svg',    label: 'Fieldy',         short: 'F' },
  twilio:    { src: '/source-marks/twilio.svg',    label: 'Twilio',         short: 'T' },
  hubspot:   { src: '/source-marks/hubspot.svg',   label: 'HubSpot',        short: 'HS' },
  traveltrucks: { src: '/source-marks/traveltrucks.svg', label: 'TravelTrucks OS', short: 'TT' },
};

/**
 * Resolve a tile-id → source key.
 * Lists (list-tile-xxx) and any custom tiles default to TravelTrucks-native.
 */
export const sourceFor = (tileId) => {
  if (!tileId) return 'traveltrucks';
  if (TILE_SOURCE_MAP[tileId]) return TILE_SOURCE_MAP[tileId];
  // Dynamic ids: `list-xxxx`, `client-xxxx`, `single-client-*` — all OS-native.
  return 'traveltrucks';
};

/**
 * SourceMark — the actual visual badge. Fixed size, drop-shadow to lift it
 * off the tile background, alt-text = the service name. On image-load error
 * (missing asset, offline) falls back to a monogram chip in brand colours
 * rather than a broken-image icon.
 */
export const SourceMark = ({ source = 'traveltrucks', size = 18, testId }) => {
  const [broke, setBroke] = useState(false);
  const meta = SOURCE_META[source] || SOURCE_META.traveltrucks;
  if (broke) {
    return (
      <span data-testid={testId || `source-mark-${source}-fallback`}
        title={meta.label}
        style={{
          width: size, height: size, borderRadius: '50%', flexShrink: 0,
          background: 'linear-gradient(135deg,#54D197,#F79640)', color: '#0E0F11',
          display: 'inline-grid', placeItems: 'center',
          fontFamily: 'Source Sans 3, sans-serif', fontWeight: 900, fontSize: Math.max(8, size * 0.42),
          letterSpacing: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
        }}>{meta.short}</span>
    );
  }
  return (
    <img src={meta.src} alt={meta.label} title={meta.label}
      data-testid={testId || `source-mark-${source}`}
      onError={() => setBroke(true)}
      style={{
        width: size, height: size, flexShrink: 0, borderRadius: '50%',
        display: 'block', objectFit: 'cover',
        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.35))',
      }} />
  );
};
